/**
 * Kleda Client App - Layout Components
 * "Stil premium" design — rust, witruimte, status in één blik
 */

import { ReactNode } from 'react'
import { KLEDA_CONTACT } from '@platform/kleda-content'
import { getViewConfig, type OrderData } from '../types'
import { Timeline } from './Timeline'
import { OrderSummary } from './OrderSummary'
import { SocialCard } from './SocialCard'
import { KLEDA_SOCIAL, toonSocialKaart } from '../lib/social'

// =============================================================================
// STATUS CHIP — status in één blik in de header
// =============================================================================

type ChipTone = 'amber' | 'paid' | 'neutral'

interface StatusChip {
  label: string
  tone: ChipTone
}

const KLAAR_STATUSSEN = ['Teruggebracht', 'Afgerond', 'Zelf opgehaald']
const INGEPLAND_STATUSSEN = ['Ophalen ingepland', 'Terugbrengen ingepland']
const BEHANDELING_STATUSSEN = ['Opgehaald', 'Zelf gebracht', 'Aan het reinigen', 'Kwaliteitscontrole']

/**
 * Afgeleid van bestaande betaalStatus + status — geen nieuwe data.
 */
export function getStatusChip(order: OrderData): StatusChip | null {
  if (KLAAR_STATUSSEN.includes(order.status)) {
    return { label: 'Afgerond', tone: 'neutral' }
  }
  if (order.phaseType === 'delivery' && order.betaalStatus === 'betaald') {
    return { label: 'Betaald', tone: 'paid' }
  }
  if (order.phaseType === 'delivery' && order.betaalLink && order.betaalStatus !== 'betaald') {
    return { label: 'Betaling openstaand', tone: 'amber' }
  }
  if (INGEPLAND_STATUSSEN.includes(order.status)) {
    return { label: 'Ingepland', tone: 'neutral' }
  }
  // De klant moet iets doen: dat mag de kop meteen zeggen, niet pas de
  // tekst eronder. Zelfde amber als een openstaande betaling.
  if (getViewConfig(order.status).view === 'kies-afspraak') {
    return { label: 'Kies een moment', tone: 'amber' }
  }
  if (BEHANDELING_STATUSSEN.includes(order.status)) {
    return { label: 'In behandeling', tone: 'neutral' }
  }
  return null
}

const CHIP_TONE_CLASSES: Record<ChipTone, string> = {
  amber: 'bg-chip-amber border border-chip-amber-line text-chip-amber-ink',
  paid: 'bg-mint-pale border border-mint-soft text-secondary-ink',
  neutral: 'bg-track border border-track text-ink-soft',
}

function StatusChipBadge({ chip }: { chip: StatusChip }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap text-[12px] font-bold px-sm py-[3px] rounded-full ${CHIP_TONE_CLASSES[chip.tone]}`}
    >
      {chip.label}
    </span>
  )
}

// =============================================================================
// HEADER
// =============================================================================

interface HeaderProps {
  orderNummer?: string
  chip?: StatusChip | null
}

export function Header({ orderNummer, chip }: HeaderProps) {
  return (
    <header className="sticky top-0 z-30 bg-white brand-line">
      <div className="max-w-5xl mx-auto px-md h-14 flex items-center justify-between">
        {/* Logo */}
        <a href="https://kleda.nl" aria-label="Naar de website van Kleda" className="flex items-center">
          <img
            src="/logo.svg"
            alt="Kleda"
            className="h-8 w-auto object-contain"
          />
        </a>

        {/* Order nummer + status-chip */}
        <div className="flex items-center gap-sm">
          {orderNummer && (
            <span className="hidden sm:inline text-[12.5px] text-ink-faint font-medium">
              {orderNummer}
            </span>
          )}
          {chip && <StatusChipBadge chip={chip} />}
        </div>
      </div>
    </header>
  )
}

// =============================================================================
// FOOTER
// =============================================================================

export function Footer() {
  return (
    <footer className="border-t border-track mt-auto">
      <div className="max-w-container mx-auto px-md py-lg">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-md text-body-sm text-ink-faint">
          <p>
            Vragen? Bel ons op{' '}
            <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="font-semibold text-primary-dark">
              {KLEDA_CONTACT.telefoonWeergave}
            </a>
          </p>
          <div className="flex items-center gap-lg">
            <a
              href={KLEDA_SOCIAL.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Instagram
            </a>
            <a
              href={KLEDA_SOCIAL.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Facebook
            </a>
            <a
              href="https://kleda.nl/privacy"
              className="hover:text-primary transition-colors"
            >
              Privacy
            </a>
            <a
              href="https://kleda.nl/voorwaarden"
              className="hover:text-primary transition-colors"
            >
              Voorwaarden
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}

// =============================================================================
// CONTAINER
// =============================================================================

interface ContainerProps {
  children: ReactNode
  className?: string
}

export function Container({ children, className = '' }: ContainerProps) {
  return (
    <div className={`max-w-container mx-auto px-md py-lg sm:py-xl ${className}`}>
      {children}
    </div>
  )
}

// =============================================================================
// PAGE LAYOUT (legacy — used by standalone pages like OndertapijtView)
// =============================================================================

interface PageLayoutProps {
  children: ReactNode
  orderNummer?: string
}

export function PageLayout({ children, orderNummer }: PageLayoutProps) {
  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <Header orderNummer={orderNummer} />
      <main className="flex-1">
        <Container>{children}</Container>
      </main>
      <Footer />
    </div>
  )
}

// =============================================================================
// PORTAL LAYOUT — New persistent layout with Timeline + OrderSummary
// =============================================================================

interface PortalLayoutProps {
  children: ReactNode
  order: OrderData
  token?: string
  vlekkenbaasHighlighted?: boolean
  onVlekkenbaasSuccess?: () => void
  onPaymentInitiated?: () => void
  isPaymentPolling?: boolean
  paymentTimedOut?: boolean
  resetPaymentTimedOut?: () => void
  /** Callback wanneer de "Vervolg op uw klacht"-pill in Timeline geklikt wordt. */
  onOpenKlachtDetail?: () => void
  /** Order-data herladen — maakt kleed-wijzigen vanuit het bestelling-overzicht mogelijk */
  onRefresh?: () => void
}

/**
 * Portaal-layout: Header (met status-chip) → [Voortgangslijn + Main Content | OrderSummary] → Footer
 *
 * Desktop (lg+): 2-kolom layout met sidebar
 * Mobile: gestapeld met inklapbare OrderSummary
 */
export function PortalLayout({ children, order, token, onPaymentInitiated, isPaymentPolling, paymentTimedOut, resetPaymentTimedOut, onOpenKlachtDetail, onRefresh }: PortalLayoutProps) {
  const isDeliveryPhase = order.phaseType === 'delivery'

  // Vervolg-WO indicator boven Timeline
  const vervolgOpKlacht = order.isVervolgWo && order.klachtParentNummer
    ? { klachtNummer: order.klachtParentNummer, onClick: onOpenKlachtDetail }
    : undefined

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <Header orderNummer={order.werkOrderNummer} chip={getStatusChip(order)} />

      {/* Main content area */}
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-md py-lg sm:py-xl">
          {/* Voortgangslijn — dun, altijd zichtbaar, op canvas */}
          <Timeline
            status={order.status}
            zelf={{ brengen: order.zelfBrengen === true, halen: order.zelfHalen === true }}
            vervolgOpKlacht={vervolgOpKlacht}
            className="mb-lg sm:mb-xl"
          />

          <div className="lg:grid lg:grid-cols-[1fr_280px] lg:gap-xl">
            {/* Main content */}
            <div className="min-w-0">
              {children}
            </div>

            {/* Sidebar (desktop) */}
            <aside className="hidden lg:block">
              <div className="sticky top-4 space-y-md">
                {/* Bestelling eerst (het anker), upsell eronder */}
                <OrderSummary order={order} token={token} onRefresh={onRefresh} onPaymentInitiated={onPaymentInitiated} isPaymentPolling={isPaymentPolling} paymentTimedOut={paymentTimedOut} resetPaymentTimedOut={resetPaymentTimedOut} />
                <p className="px-xs text-body-sm text-ink-faint leading-relaxed">
                  Vragen?{' '}
                  <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="font-semibold text-primary-dark">
                    {KLEDA_CONTACT.telefoonWeergave}
                  </a>
                  <br />
                  {KLEDA_CONTACT.klantenservice}
                </p>
              </div>
            </aside>
          </div>

          {/* Order Summary (mobile — below content, not during delivery as it's shown inline) */}
          {!isDeliveryPhase && (
            <div className="lg:hidden mt-lg">
              <OrderSummary order={order} token={token} onRefresh={onRefresh} onPaymentInitiated={onPaymentInitiated} isPaymentPolling={isPaymentPolling} paymentTimedOut={paymentTimedOut} resetPaymentTimedOut={resetPaymentTimedOut} />
            </div>
          )}
          {toonSocialKaart(order) && (
            <div className="lg:grid lg:grid-cols-[1fr_280px] lg:gap-xl">
              <SocialCard />
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}
